import { memo, useCallback, useState } from 'react';
import { MessageStatus } from '@avanta/shared/constants/chat';
import { useActiveOtherUserId } from '@avanta/shared/stores/chat/conversations';
import type { OptimisticMessage } from '@avanta/shared/types/chat';
import { cn } from '@avanta/shared/utils/cn';
import { Content, Actions, EditMessage } from './components';

interface MessageProps {
  message: OptimisticMessage;
}

const MessageComponent = ({ message }: MessageProps): React.ReactElement => {
  const [showOptions, setShowOptions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const otherUserId = useActiveOtherUserId();
  const handleMouseEnter = useCallback(() => setShowOptions(true), []);
  const handleMouseLeave = useCallback(() => setShowOptions(false), []);
  const handleEdit = useCallback(() => setIsEditing(true), []);
  const handleCloseEdit = useCallback(() => setIsEditing(false), []);
  const isMine = message.userId !== otherUserId;
  const isDeleted = message.deleted;
  const isSending = message.status === MessageStatus.sending;
  const notOptions = !showOptions || isDeleted || isEditing || isSending;
  const notShowEdit = notOptions || !!message.text;
  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      id={message.timetoken}
      className={cn('flex', isMine && 'justify-end')}
    >
      <div
        className={cn(
          'flex flex-col am-sm:flex-row am-sm:items-center items-end gap-2',
          isMine && 'am-sm:flex-row-reverse items-start',
          isEditing && 'w-full max-w-150'
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-2 w-fit min-w-70 max-w-150 px-4 py-2 rounded-2xl bg-gray-100',
            isMine ? 'bg-plum-200 rounded-tr-none' : 'rounded-tl-none',
            message.status === MessageStatus.failed && 'border border-red-300',
            isEditing && 'border border-gray-200 bg-white w-full'
          )}
        >
          {isEditing ? (
            <EditMessage message={message} onClose={handleCloseEdit} />
          ) : (
            <Content message={message} />
          )}
        </div>
        <Actions
          message={message}
          notShowEdit={notShowEdit}
          notShow={notOptions}
          onEdit={handleEdit}
        />
      </div>
    </div>
  );
};

export const Message = memo(MessageComponent);
